import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLElement} [liveRegion]
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {

  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** @type {number} */
  currentItemCount = 0;

  connectedCallback() {
    super.connectedCallback();

    // Initial cart count
    fetch('/cart.js')
      .then((res) => res.json())
      .then((cart) => {
        this.currentItemCount = cart.item_count || 0;
      });

    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);

    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {

    if (!isMobileBreakpoint()) return;

    // Prevent duplicate pushState
    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();

    window.addEventListener(
      'popstate',
      this.#handlePopState,
      { signal: this.#historyAbortController.signal }
    );
  };

  #handleHistoryClose = () => {

    this.#historyAbortController?.abort();

    // IMPORTANT:
    // Removed history.back() because it was reopening drawer
    // after outside click / close action.
  };

  #handlePopState = async () => {

    if (
      !history.state?.cartDrawerOpen &&
      this.refs.dialog?.open
    ) {

      this.refs.dialog.style.setProperty(
        '--dialog-drawer-closing-animation',
        'none'
      );

      await this.closeDialog();

      this.refs.dialog.style.removeProperty(
        '--dialog-drawer-closing-animation'
      );
    }
  };

  /**
   * Handles cart add events
   * Opens drawer only when cart count increases
   *
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {

    const itemCount = event.detail.resource?.item_count || 0;
    const previousCount = this.currentItemCount || 0;

    // Open only when items increase
    if (
      itemCount > previousCount &&
      this.hasAttribute('auto-open')
    ) {
      this.showDialog();
    }

    this.currentItemCount = itemCount;

    this.#announceCartCount(itemCount);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   *
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {

    const liveRegion =
      /** @type {HTMLElement | undefined} */
      (this.refs.liveRegion);

    if (
      !this.refs.dialog?.open ||
      !liveRegion ||
      cartCount === undefined
    ) {
      return;
    }

    liveRegion.textContent =
      `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {

    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked
     * to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {

      const installmentsContent =
        document.querySelector('shopify-payment-terms')?.shadowRoot;

      const cta =
        installmentsContent?.querySelector('#shopify-installments-cta');

      cta?.addEventListener('click', this.closeDialog, { once: true });

    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {

    const { dialog } =
      /** @type {Refs} */
      (this.refs);

    if (!dialog) return;

    const content =
      dialog.querySelector('.cart-drawer__content');

    const summary =
      dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {

      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold ? 'false' : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}